import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { promoCodesApi, type ActiveDiscountResponse } from '../../api/promoCodes';

/**
 * PromoCodeCard — встраиваемый блок для ввода и активации промокода.
 * При успешной активации показывает текущую активную скидку.
 * Используется на экране подписок (SubscriptionPlansScreen) и в профиле.
 */
export function PromoCodeCard() {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeDiscount, setActiveDiscount] = useState<ActiveDiscountResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadActiveDiscount = async () => {
    try {
      const data = await promoCodesApi.getActiveDiscount();
      setActiveDiscount(data);
    } catch (e) {
      // ignore — отсутствие активной скидки не критично
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadActiveDiscount();
  }, []);

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault?.();
    if (!code.trim()) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const validation = await promoCodesApi.validate(code.trim());
      if (!validation.valid) {
        setError(validation.reason || 'Промокод недействителен');
        setSubmitting(false);
        return;
      }

      const result = await promoCodesApi.redeem(code.trim());
      setSuccess(
        `Промокод применён! Скидка ${result.discount_percent}% действует до ${formatDate(
          result.discount_expires_at
        )}.`
      );
      setCode('');
      await loadActiveDiscount();
      // Сообщаем остальным компонентам (SubscriptionPlansScreen, TierChangeConfirmScreen),
      // чтобы они перечитали активную скидку и пересчитали отображаемые цены.
      await queryClient.invalidateQueries({ queryKey: ['active-discount'] });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Не удалось применить промокод';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  // Если уже есть активная скидка — показываем её и не даём ввести новый
  if (activeDiscount?.has_active_discount) {
    return (
      <Card>
        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Активная скидка
        </h3>
        <div className="text-sm space-y-1" style={{ color: 'var(--text-primary)' }}>
          <div>
            Промокод:{' '}
            <span className="font-mono font-bold" style={{ color: 'var(--accent)' }}>
              {activeDiscount.promo_code}
            </span>
          </div>
          <div>
            Скидка: <span className="font-bold">{activeDiscount.discount_percent}%</span>
          </div>
          {activeDiscount.discount_expires_at && (
            <div style={{ color: 'var(--text-hint)' }}>
              Действует до {formatDate(activeDiscount.discount_expires_at)}
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
        Промокод
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Введите промокод"
          className="w-full px-3 py-2 rounded-lg border text-sm font-mono"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            borderColor: 'var(--separator)',
          }}
          disabled={submitting}
          maxLength={50}
        />
        {error && (
          <p className="text-xs" style={{ color: '#ef4444' }}>
            {error}
          </p>
        )}
        {success && (
          <p className="text-xs" style={{ color: '#22c55e' }}>
            {success}
          </p>
        )}
        <Button onClick={handleSubmit} disabled={submitting || !code.trim()} fullWidth variant="primary">
          {submitting ? 'Проверка…' : 'Применить'}
        </Button>
      </form>
    </Card>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
